import { PresetCriterias, ScreenerType } from '@/features/api/screener/screenerTypes';
import {
  ComputedBadge,
  EstimatedBadge,
  InfoBadge,
  ListedBadge,
  MarketBadge,
  OfficialBadge,
  ReportedBadge,
} from '../Badge';
import DoubleRange from '../DoubleRange';
import { SourceType } from '../Table4/types';
import { FilterListData } from './type';

export const informations: Partial<Record<ScreenerType, FilterListData[]>> = {
  funds: [
    {
      path: 'official_launch_date',
      withoutUnit: true,
      isDate: false,
      withoutNumberFormatter: true,
      header: 'launch_date',
    },
    {
      path: 'reported_last_report_date',
      withoutUnit: true,
      isDate: true,
      withoutNumberFormatter: true,
      header: 'last_report_date',
    },
    {
      path: 'market_dividend_date',
      withoutUnit: true,
      isDate: true,
      withoutNumberFormatter: true,
      header: 'm_dividend_date',
    },
    {
      path: 'reported_units_outstanding',
      withoutUnit: true,
      header: 'r_units_outstanding',
    },
    { path: 'reported_total_rental_space', header: 'r_total_rental_space' },
    {
      path: 'reported_commercial_rental_space',
      header: 'r_commercial_rental_space',
    },
    {
      path: 'reported_residential_rental_space',
      header: 'r_residential_rental_space',
    },
    { path: 'reported_mixed_rental_space', header: 'r_mixed_rental_space' },
    { path: 'reported_plots_rental_space', header: 'r_plots_rental_space' },
    { path: 'reported_other_rental_space', header: 'r_other_rental_space' },
    { path: 'computed_lots_nb', withoutUnit: true, header: 'lots_nb' },
    {
      path: 'reported_building_groups_nb',
      withoutUnit: true,
      header: 'building_groups_nb',
    },
    { path: 'computed_buildings_nb', withoutUnit: true, header: 'buildings_nb' },
    { path: 'computed_dwellings_nb', withoutUnit: true, header: 'dwellings_nb' },
    {
      path: 'official_isin',
      withoutUnit: true,
      nonFilterable: true,
      header: 'isin',
    },
    {
      path: 'official_bloomberg_ticker',
      withoutUnit: true,
      nonFilterable: true,
      header: 'bloomberg_ticker',
    },
  ],
  buildings: [
    {
      path: 'official_construction_date',
      header: 'construction_date',
      withoutUnit: true,
      withoutNumberFormatter: true,
    },
    { path: 'official_dwellings_nb', withoutUnit: true, header: 'dwellings_nb' },
    { path: 'official_floors_nb', withoutUnit: true, header: 'floors_nb' },
    { path: 'official_ground_area', header: 'ground_area' },
    { path: 'computed_total_dwellings_area', header: 'residential_area' },
    { path: 'computed_total_commercial_area', header: 'commercial_area' },
    { path: 'computed_total_area', header: 'total_area' },
    { path: 'reported_rental_area', header: 'r_rental_space' },
  ],
  transactions: [
    {
      path: 'construction_year',
      withoutUnit: true,
      withoutNumberFormatter: true,
    },
    { path: 'dwellings_nb', withoutUnit: true },
    { path: 'floors_nb', withoutUnit: true },
    { path: 'ground_area' },
    { path: 'total_area' },
  ],
  dwellings: [
    {
      path: 'official_construction_date',
      header: 'construction_date',
      withoutUnit: true,
      withoutNumberFormatter: true,
    },
    { path: 'official_floor', withoutUnit: true, header: 'floor' },
    { path: 'official_rooms_nb', withoutUnit: true, header: 'rooms_nb' },
    { path: 'official_area', header: 'area' },
    { path: 'computed_total_area', header: 'total_area' },
  ],
};
export const accountings: Partial<Record<ScreenerType, FilterListData[]>> = {
  funds: [
    { path: 'reported_rental_income', header: 'r_rental_income' },
    { path: 'reported_building_expenses', header: 'r_building_expenses' },
    { path: 'reported_capital_distribution', header: 'r_capital_distribution' },
    {
      path: 'reported_construction_and_works_fees',
      header: 'r_construction_and_works_fees',
    },
    {
      path: 'reported_estimated_liquidation_taxes',
      header: 'r_estimated_liquidation_taxes',
    },
    {
      path: 'reported_maintenance_and_repairs_expenses',
      header: 'r_maintenance_and_repairs_expenses',
    },
    {
      path: 'reported_profit_and_capital_taxes',
      header: 'r_profit_and_capital_taxes',
    },
    {
      path: 'reported_property_management_fees',
      header: 'r_property_management_fees',
    },
    { path: 'reported_property_tax', header: 'r_property_tax' },
    {
      path: 'reported_purchase_and_sale_fees',
      header: 'r_purchase_and_sale_fees',
    },
    {
      path: 'reported_realised_capital_gains_and_losses',
      header: 'r_realised_capital_gains_and_losses',
    },
    { path: 'reported_realised_income', header: 'r_realised_income' },
    { path: 'reported_retained_earnings', header: 'r_retained_earnings' },
    {
      path: 'reported_revenue_from_postal_and_bank_accounts',
      header: 'r_revenue_from_postal_and_bank_accounts',
    },
    {
      path: 'reported_short_term_interest_bearing_mortgages',
      header: 'r_short_term_interest_bearing_mortgages',
    },
    {
      path: 'reported_short_term_liabilities',
      header: 'r_short_term_liabilities',
    },
    { path: 'reported_taxes', header: 'r_taxes' },
    { path: 'reported_total_cash', header: 'r_total_cash' },
    { path: 'reported_total_dividend', header: 'r_total_dividend' },
    { path: 'reported_total_expenses', header: 'r_total_expenses' },
    { path: 'reported_total_income', header: 'r_total_income' },
    { path: 'reported_total_interest', header: 'r_total_interest' },
    { path: 'reported_total_liabilities', header: 'r_total_liabilities' },
    { path: 'reported_total_properties', header: 'r_total_properties' },
    { path: 'reported_total_revenue', header: 'r_total_revenue' },
    {
      path: 'reported_unrealised_capital_gains_and_losses',
      header: 'r_unrealised_capital_gains_and_losses',
    },
    {
      path: 'reported_valuation_and_auditing_expenses',
      header: 'r_valuation_and_auditing_expenses',
    },
    { path: 'reported_valuation_expenses', header: 'r_valuation_expenses' },
    {
      path: 'reported_absolute_custodian_bank_fees',
      header: 'r_absolute_custodian_bank_fees',
    },
    {
      path: 'reported_absolute_management_fees',
      header: 'r_absolute_management_fees',
    },
    {
      path: 'reported_administration_of_buildings',
      header: 'r_administration_of_buildings',
    },
    { path: 'reported_auditing_expenses', header: 'r_auditing_expenses' },
    { path: 'reported_custodian_bank_fees', header: 'r_custodian_bank_fees' },
    { path: 'reported_management_fees', header: 'r_management_fees' },
    {
      path: 'reported_share_buyback_commissions',
      header: 'r_share_buyback_commissions',
    },
    {
      path: 'reported_share_issue_commissions',
      header: 'r_share_issue_commissions',
    },
    { path: 'reported_ter_gav', header: 'r_ter_gav' },
    { path: 'reported_ter_mv', header: 'r_ter_mv' },
    { path: 'reported_ter_nav', header: 'r_ter_nav' },
    { path: 'computed_current_liabilities', header: 'c_current_liabilities' },
    { path: 'computed_operating_profit', header: 'c_operating_profit' },
    {
      path: 'reported_dividend_distribution',
      header: 'r_dividend_distribution',
    },
    {
      path: 'reported_dividend_from_capital_gains',
      header: 'r_dividend_from_capital_gains',
    },
    {
      path: 'reported_dividend_from_direct_rental_income',
      header: 'r_dividend_from_direct_rental_income',
    },
    {
      path: 'reported_dividend_from_indirect_rental_income',
      header: 'r_dividend_from_indirect_rental_income',
    },
    {
      path: 'reported_long_term_interest_bearing_mortgages',
      header: 'r_long_term_interest_bearing_mortgages',
    },
    {
      path: 'reported_long_term_liabilities',
      header: 'r_long_term_liabilities',
    },
    { path: 'reported_mortgage_interest', header: 'r_mortgage_interest' },
    { path: 'reported_negative_interest', header: 'r_negative_interest' },
    {
      path: 'reported_net_assets_at_beginning_of_the_financial_year',
      header: 'r_net_assets_at_beginning_of_the_financial_year',
    },
    {
      path: 'reported_net_assets_before_estimated_liquidation_taxes',
      header: 'r_net_assets_before_estimated_liquidation_taxes',
    },
    { path: 'reported_net_income', header: 'r_net_income' },
    {
      path: 'reported_net_income_available_for_distribution',
      header: 'r_net_income_available_for_distribution',
    },
    { path: 'reported_nopat', header: 'r_nopat' },
    {
      path: 'reported_ordinary_income_carried_forward_from_previous_financial_year',
      header: 'r_ordinary_income_carried_forward_from_previous_financial_year',
    },
    { path: 'reported_other_assets', header: 'r_other_assets' },
    { path: 'reported_other_expenses', header: 'r_other_expenses' },
    { path: 'reported_other_interest', header: 'r_other_interest' },
    {
      path: 'reported_other_long_term_liabilities',
      header: 'r_other_long_term_liabilities',
    },
    { path: 'reported_other_revenue', header: 'r_other_revenue' },
    {
      path: 'reported_other_short_term_liabilities',
      header: 'r_other_short_term_liabilities',
    },
    {
      path: 'reported_gav_before_distribution',
      header: 'r_gav_before_distribution',
    },
    { path: 'reported_market_cap', header: 'r_market_cap' },
    {
      path: 'reported_nav_before_distribution',
      header: 'r_nav_before_distribution',
    },
    {
      path: 'reported_tga_after_distribution',
      header: 'r_tga_after_distribution',
    },
    {
      path: 'reported_tga_before_distribution',
      header: 'r_tga_before_distribution',
    },
    {
      path: 'reported_tna_after_distribution',
      header: 'r_tna_after_distribution',
    },
    {
      path: 'reported_tna_before_distribution',
      header: 'r_tna_before_distribution',
    },
    {
      path: 'reported_unrealised_gains_and_losses_including_var_est_liq_tax',
      header: 'r_unrealised_gains_and_losses_including_var_est_liq_tax',
    },
  ],
};
export const financials: Partial<Record<ScreenerType, FilterListData[]>> = {
  funds: [
    { path: 'computed_debt_ratio', header: 'c_debt_ratio' },
    { path: 'computed_current_ratio', header: 'c_current_ratio' },
    { path: 'computed_ebit_margin', header: 'c_ebit_margin' },
    { path: 'computed_loan_to_value', header: 'c_loan_to_value' },
    { path: 'computed_net_initial_yield', header: 'c_net_initial_yield' },
    { path: 'computed_payout_ratio', header: 'c_payout_ratio' },
    { path: 'computed_pe_ratio', header: 'c_pe_ratio', withoutUnit: true },
    { path: 'computed_price_to_book_value', header: 'c_price_to_book_value' },
    { path: 'computed_roa', header: 'c_roa' },
    { path: 'computed_roce', header: 'c_roce' },
    { path: 'computed_roe', header: 'c_roe' },
    { path: 'computed_total_cash_per_share', header: 'c_total_cash_per_share' },
    { path: 'reported_debt_ratio', header: 'r_debt_ratio' },
    { path: 'reported_dividend_yield', header: 'r_dividend_yield' },
    { path: 'computed_earnings_per_share', header: 'c_earnings_per_share' },
    { path: 'computed_revenue_per_share', header: 'c_revenue_per_share' },
    { path: 'reported_ebit', header: 'r_ebit' },
    { path: 'reported_ebit_margin', header: 'r_ebit_margin' },
    { path: 'computed_median_cost_of_debt', header: 'c_median_cost_of_debt' },
    { path: 'computed_median_maturity', header: 'c_median_maturity' },
    { path: 'reported_payout_ratio', header: 'r_payout_ratio' },
    { path: 'reported_performance', header: 'r_performance' },
    { path: 'reported_rental_losses', header: 'r_rental_losses' },
    { path: 'reported_roa', header: 'r_roa' },
    { path: 'reported_roce', header: 'r_roce' },
    { path: 'reported_roe', header: 'r_roe' },
    { path: 'reported_roi', header: 'r_roi' },
    { path: 'reported_roic', header: 'r_roic' },
    { path: 'reported_wault', header: 'r_wault' },
    {
      path: 'computed_weighted_average_cost_of_debt',
      header: 'c_weighted_average_cost_of_debt',
    },
    {
      path: 'computed_weighted_average_maturity',
      header: 'c_weighted_average_maturity',
    },
    { path: 'market_dividend', header: 'm_dividend' },
    { path: 'reported_capital_employed', header: 'r_capital_employed' },
    { path: 'computed_total_net_rent', header: 'net_rent' },
    { path: 'estimated_total_net_rent', header: 'potential_total_income' },
    { path: 'estimated_total_market_value' },
  ],
  buildings: [
    { path: 'estimated_total_net_rent', header: 'potential_net_rent' },
    {
      path: 'estimated_total_net_rent_per_m2',
      header: 'potential_net_rent_per_m2',
    },
    { path: 'estimated_market_value', header: 'potential_market_value' },
    {
      path: 'estimated_market_value_per_m2',
      header: 'potential_market_value_per_m2',
    },
    { path: 'estimated_total_net_charges', header: 'potential_charges' },
    {
      path: 'estimated_total_net_charges_per_m2',
      header: 'potential_charges_per_m2',
    },
    { path: 'estimated_roa', header: 'potential_roa' },
    { path: 'listed_total_net_rent', header: 'real_net_rent' },
    { path: 'listed_total_charges', header: 'real_charges' },
    { path: 'reported_market_value', header: 'r_market_value' },
    { path: 'reported_real_rent', header: 'r_real_rent' },
    { path: 'reported_target_rent', header: 'r_target_rent' },
    { path: 'reported_cost_price', header: 'r_cost_price' },
    { path: 'reported_roa', header: 'r_gross_yield' },
  ],
  dwellings: [
    { path: 'estimated_net_rent', header: 'potential_net_rent' },
    {
      path: 'estimated_net_rent_per_m2',
      header: 'potential_net_rent_per_m2',
    },
    { path: 'estimated_market_value', header: 'potential_market_value' },
    {
      path: 'estimated_market_value_per_m2',
      header: 'potential_market_value_per_m2',
    },
    { path: 'estimated_charges', header: 'potential_charges' },
    {
      path: 'estimated_charges_per_m2',
      header: 'potential_charges_per_m2',
    },
    { path: 'estimated_roa', header: 'potential_roa' },
    { path: 'listed_net_rent', header: 'real_net_rent' },
    { path: 'listed_charges', header: 'real_charges' },
    {
      path: 'estimated_net_rent_monthly',
      nonFilterable: true,
      header: 'potential_net_rent_monthly',
    },
    {
      path: 'estimated_charges_monthly',
      nonFilterable: true,
      header: 'potential_charges_monthly',
    },
    {
      path: 'listed_net_rent_monthly',
      nonFilterable: true,
      header: 'real_net_rent_monthly',
    },
    {
      path: 'listed_charges_monthly',
      nonFilterable: true,
      header: 'real_charges_monthly',
    },
  ],
};
export const performances: Partial<Record<ScreenerType, FilterListData[]>> = {
  funds: [
    { path: 'market_market_cap', header: 'm_market_cap' },
    { path: 'reported_share_price', header: 'r_share_price' },
    {
      path: 'computed_gav_after_distribution',
      header: 'c_gav_after_distribution',
    },
    {
      path: 'computed_nav_after_distribution',
      header: 'c_nav_after_distribution',
    },
    { path: 'reported_agio_at_year_end', header: 'r_agio_at_year_end' },
    {
      path: 'computed_total_agio_at_year_end',
      header: 'c_total_agio_at_year_end',
    },
    {
      path: 'computed_current_agio_after_distribution',
      header: 'c_current_agio_after_distribution',
    },
    {
      path: 'computed_current_agio_compounded',
      header: 'c_current_agio_compounded',
    },
    { path: 'computed_gav_compounded', header: 'c_gav_compounded' },
    { path: 'computed_nav_compounded', header: 'c_nav_compounded' },
    { path: 'computed_tga_compounded', header: 'c_tga_compounded' },
    { path: 'computed_tna_compounded', header: 'c_tna_compounded' },
    {
      path: 'computed_total_current_agio_after_distribution',
      header: 'c_total_current_agio_after_distribution',
    },
    {
      path: 'computed_total_current_agio_compounded',
      header: 'c_total_current_agio_compounded',
    },
    { path: 'market_alpha', header: 'alpha' },
    { path: 'market_beta', withoutUnit: true, header: 'beta' },
    { path: 'market_share_price', header: 'm_share_price' },
    { path: 'market_share_price_var', header: 'm_share_price_var' },
    { path: 'market_dividend_yield', header: 'm_dividend_yield' },
    { path: 'market_fifty_two_weeks_high', header: 'fifty_two_weeks_high' },
    { path: 'market_fifty_two_weeks_low', header: 'fifty_two_weeks_low' },
    {
      path: 'market_five_days_avg_volume',
      withoutUnit: true,
      header: 'five_days_avg_volume',
    },
    {
      path: 'market_five_years_avg_dividend_yield',
      header: 'five_years_avg_dividend_yield',
    },
    { path: 'market_five_years_return', header: 'five_years_return' },
    {
      path: 'computed_three_years_total_return',
      header: 'c_three_years_total_return',
    },
    {
      path: 'computed_five_years_total_return',
      header: 'c_five_years_total_return',
    },
    { path: 'market_high', header: 'high' },
    {
      path: 'market_hist_volatility_180_days',
      withoutUnit: true,
      header: 'hist_volatility_180_days',
    },
    {
      path: 'market_hist_volatility_250_days',
      withoutUnit: true,
      header: 'hist_volatility_250_days',
    },
    {
      path: 'market_hist_volatility_30_days',
      withoutUnit: true,
      header: 'hist_volatility_30_days',
    },
    {
      path: 'market_hist_volatility_90_days',
      withoutUnit: true,
      header: 'hist_volatility_90_days',
    },
    {
      path: 'market_implied_volatility',
      withoutUnit: true,
      header: 'implied_volatility',
    },
    { path: 'market_last', header: 'last' },
    { path: 'market_low', header: 'low' },
    { path: 'market_mtd_net_return', header: 'mtd_net_return' },
    {
      path: 'market_one_month_avg_volume',
      withoutUnit: true,
      header: 'one_month_avg_volume',
    },
    { path: 'market_one_month_return', header: 'one_month_return' },
    {
      path: 'market_one_year_avg_volume',
      withoutUnit: true,
      header: 'one_year_avg_volume',
    },
    { path: 'market_one_year_return', header: 'one_year_return' },
    { path: 'market_open', header: 'open' },
    { path: 'market_qtd_net_return', header: 'qtd_net_return' },
    { path: 'market_r2', withoutUnit: true, header: 'r2' },
    {
      path: 'market_six_months_avg_volume',
      withoutUnit: true,
      header: 'six_months_avg_volume',
    },
    { path: 'market_six_months_return', header: 'six_months_return' },
    {
      path: 'market_three_months_avg_volume',
      withoutUnit: true,
      header: 'three_months_avg_volume',
    },
    { path: 'market_three_months_return', header: 'three_months_return' },
    { path: 'market_three_years_return', header: 'three_years_return' },
    { path: 'market_turnover', header: 'turnover' },
    { path: 'market_volume', withoutUnit: true, header: 'volume' },
    { path: 'market_ytd_net_return', header: 'ytd_net_return' },
    { path: 'market_one_year_total_return', header: 'one_year_total_return' },
    {
      path: 'computed_three_years_total_return',
      header: 'three_years_total_return',
    },
    {
      path: 'market_five_years_total_return',
      header: 'five_years_total_return',
    },
    { path: 'market_vwap', nonFilterable: true, header: 'vwap' },
  ],
};
export const esgs: Partial<Record<ScreenerType, FilterListData[]>> = {
  funds: [
    { path: 'official_press_score_environmental', withoutUnit: true, header: 'environmental' },
    { path: 'official_press_score_social', withoutUnit: true, header: 'social' },
    { path: 'official_press_score_governance', withoutUnit: true, header: 'governance' },
    { path: 'official_press_score_esg', withoutUnit: true, header: 'esg' },
    { path: 'estimated_co2_scope_1_2_per_m2', header: 'total_co2' },
    { path: 'estimated_energy_intensity', header: 'energy_intensity' },
    { path: 'reported_co2' },
    { path: 'reported_co2_per_m2' },
    { path: 'reported_energy_intensity' },
    { path: 'reported_total_energy' },
    { path: 'reported_share_fossil' },
    { path: 'reported_coverage_share_fossil' },
    { path: 'reported_heated_area' },
    { path: 'computed_energy_source_heating_type_coverage', nonFilterable: true },
  ],
};
export const locations: Partial<Record<ScreenerType, FilterListData[]>> = {
  lots: [
    {
      path: 'official_sector_nb',
      header: 'sector_nb',
      withoutUnit: true,
      nonFilterable: true,
    },
    { path: 'official_nb', header: 'lot_nb', withoutUnit: true, nonFilterable: true },
  ],
  buildings: [
    { path: 'official_zip', header: 'zip', withoutUnit: true, nonFilterable: true },
    { path: 'official_egid', header: 'egid', withoutUnit: true, nonFilterable: true },
  ],
  transactions: [
    { path: 'municipality_nb', withoutUnit: true, nonFilterable: true },
    { path: 'zip', withoutUnit: true, nonFilterable: true },
    { path: 'egid', withoutUnit: true, nonFilterable: true },
  ],
  dwellings: [{ path: 'official_zip', header: 'zip', withoutUnit: true, nonFilterable: true }],
};
export const dimensions: Partial<Record<ScreenerType, FilterListData[]>> = {
  lots: [
    { path: 'official_area', header: 'area' },
    { path: 'official_buildings_nb', header: 'buildings_nb', withoutUnit: true },
  ],
};
export const energies: Partial<Record<ScreenerType, FilterListData[]>> = {
  buildings: [
    { path: 'official_installed_solar_power', header: 'solar_existing' },
    { path: 'estimated_co2_scope_1_2_per_m2', header: 'estimated_co2_emission_per_m2' },
    { path: 'estimated_energy_intensity', header: 'estimated_idc_per_m2' },
  ],
  dwellings: [
    { path: 'estimated_co2_scope_1_2_per_m2', header: 'estimated_co2_emission_per_m2' },
    { path: 'estimated_energy_intensity', header: 'estimated_idc_per_m2' },
  ],
};
export const transactions: Partial<Record<ScreenerType, FilterListData[]>> = {
  transactions: [
    { path: 'tx_year', withoutUnit: true, withoutNumberFormatter: true },
    { path: 'tx_price' },
  ],
};

export function getFilterList(
  data: FilterListData[],
  originalSelections: PresetCriterias,
  setSelections: (partOfSelections: PresetCriterias) => void,
  reloadSlice: number
) {
  return data.map(
    ({ path, header, isDate, withoutNumberFormatter, withoutUnit, nonFilterable }) => {
      const alreadyVisible = !originalSelections.columns_to_ignore?.includes(path ?? header);
      return {
        path: path,
        visible: alreadyVisible,
        activated: nonFilterable
          ? false
          : originalSelections[(path + '_min') as keyof PresetCriterias] !== undefined ||
            originalSelections[(path + '_max') as keyof PresetCriterias] !== undefined,
        label: `table_header.${header ?? path}`,
        unit: withoutUnit ? undefined : `units.${path}`,
        sourceBadge: getSourceBadge(path ?? header),
        renderChild: nonFilterable
          ? undefined
          : (setFilterStatus: (active?: boolean, visible?: boolean) => void) => (
              <DoubleRange
                setFilterStatus={setFilterStatus}
                selectedMin={
                  originalSelections[(path + '_min') as keyof PresetCriterias] as
                    | string
                    | number
                    | undefined
                }
                selectedMax={
                  originalSelections[(path + '_max') as keyof PresetCriterias] as
                    | string
                    | number
                    | undefined
                }
                onChange={(range) => {
                  setSelections({
                    [(path + '_min') as keyof PresetCriterias]: range[0],
                    [(path + '_max') as keyof PresetCriterias]: range[1],
                  });
                }}
                withoutNumberFormatter={withoutNumberFormatter ?? false}
                isDate={isDate ?? false}
                reloadInit={reloadSlice}
              />
            ),
      };
    }
  );
}

const otherECols = ['total_co2', 'energy_intensity'];
const otherMCols = [
  'fifty_two_weeks_high',
  'fifty_two_weeks_low',
  'five_days_avg_volume',
  'five_years_avg_dividend_yield',
  'five_years_return',
  'high',
  'hist_volatility_180_days',
  'hist_volatility_250_days',
  'hist_volatility_30_days',
  'hist_volatility_90_days',
  'implied_volatility',
  'last',
  'low',
  'mtd_net_return',
  'one_month_avg_volume',
  'one_month_return',
  'one_year_avg_volume',
  'one_year_return',
  'open',
  'qtd_net_return',
  'r2',
  'six_months_avg_volume',
  'six_months_return',
  'three_months_avg_volume',
  'three_months_return',
  'three_years_return',
  'turnover',
  'volume',
  'ytd_net_return',
  'one_year_total_return',
  'three_years_total_return',
  'five_years_total_return',
  'vwap',
];
const otherRCols = [
  'building_groups_nb',
  'net_rent',
  'tx_year', // for tx
  'tx_price', // for tx
  'buyer_name',
  'seller_name',
  'address',
  'date',
  'is_sale',
  'value',

  'label',
  'cost_price',
  'start',
  'end',
  'rate',
  'rate_type',
  'currency',
  'already_reimbursed',

  'reported_cost_price',
];
const otherOCols = [
  'construction_year',
  'legal_form',
  'launch_date',
  'fund_type',
  'name',
  'bloomberg_ticker',
  'isin',
  'dwellings_nb',
  'lots_nb',
  'last_report_date',
  'esg',
  'environmental',
  'social',
  'governance',
  'esg_rating',
  'environmental_rating',
  'social_rating',
  'governance_rating',
  'building_category',
  'locality',
  'region',
  'zip',
  'canton',
  'municipality_nb',
  'municipality_name',
  'egid',
  'fund_names',
  'fund_legal_forms',
  'type',
  'construction_date',
  'floors_nb',
  'ground_area',
  'energy_source_heating',
  'energy_source_water',
  'solar_existing',
  'labels',
  'ewid',
  'street_short',

  // for plot
  'municipality_name',
  'buildings_nb',
  'sector_nb',
  'nb',
  'area',

  // for dwellings
  'floor',
  'rooms_nb',
];
const otherCCols = [
  'total_area',
  'commercial_area',
  'residential_area',
  'commercial_spaces_area',
  'alpha',
  'beta',
];

export function getSourceBadge(path: string | SourceType) {
  if (
    path.toLowerCase() === 'reported' ||
    path.startsWith('r_') ||
    path.startsWith('actual_') ||
    path.startsWith('reported_') ||
    otherRCols.includes(path)
  )
    return <ReportedBadge useAbbreviation />;
  if (
    path.toLowerCase() === 'market' ||
    path.startsWith('m_') ||
    path.startsWith('market_') ||
    otherMCols.includes(path)
  )
    return <MarketBadge useAbbreviation />;
  if (
    path.toLowerCase() === 'computed' ||
    path.startsWith('c_') ||
    path.startsWith('computed_') ||
    otherCCols.includes(path)
  )
    return <ComputedBadge useAbbreviation />;
  if (path.toLowerCase() === 'listed' || path.startsWith('real_') || path.startsWith('listed_'))
    return <ListedBadge useAbbreviation />;
  if (
    path.toLowerCase() === 'estimated' ||
    path.startsWith('potential_') ||
    path.startsWith('estimated_') ||
    otherECols.includes(path)
  )
    return <EstimatedBadge useAbbreviation />;
  if (
    path.toLowerCase() === 'official' ||
    path.startsWith('official_') ||
    otherOCols.includes(path)
  )
    return <OfficialBadge useAbbreviation />;
  return <InfoBadge useAbbreviation />;
}

export const tableSelectionKeys: Array<keyof PresetCriterias> = [
  'sort',
  'columns_to_ignore',
  'sizing',
  'order',
  'column_pinning',
  'date_max',
];

export const chartSelectionKeys: Array<keyof PresetCriterias> = [
  'chart_type',
  'x',
  'y',
  'value',
  'fields',
  'size',
  'group',
  'selected_groups',
  'benchmarks',
  'bin_nb',
  'is_relative',
  'is_base100',
  'date_min',
  'date_max',
];
